import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Tabs, Card, Tag, Space, Typography, Button, Spin, Empty,
  message, Table, Select, Alert,
} from 'antd';
import {
  DatabaseOutlined, ConsoleSqlOutlined, ReloadOutlined,
  TableOutlined, PlayCircleOutlined, HistoryOutlined, ExportOutlined,
} from '@ant-design/icons';
import {
  listDestinations, getVaultData, listConnections,
  listDagitRuns, getDagitRun,
} from '../services/dataPlatformService';
// Shared with Data Explorer's own Sync History tab — see services/syncHistory.js.
// Re-exported below because this page's tests import them from here.
import {
  buildSyncHistoryRow, buildSyncHistoryRows, filterSyncRunsForStream,
  resolveStreamAndPrefix, stripSyncAssetPrefix, syncHistoryStatusColor,
} from '../services/syncHistory';
import VaultSidebar from '../components/VaultSidebar';
import { VaultDataProvider, useVaultData } from '../contexts/VaultDataContext';
import { useSearchParams } from 'react-router';
import './DataCenter.css';

// Dagit links go through tenant-api's /admin/dagit reverse proxy
// (same-origin). See services/ui/tenant-console/src/services/dagsterLink.js.
import { dagsterRunUrl, openDagsterRun, isDagitEnabled } from '../services/dagsterLink';

// Heavy children — lazy-loaded so the initial /data/center route chunk
// doesn't carry Monaco (SQL Workbench). Each tab pane only resolves its
// module on first activation.
const DataExplorerPage = React.lazy(() => import('./DataExplorerPage'));
const SqlWorkbench = React.lazy(() => import('./SqlWorkbench'));

function PaneSpinner() {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48 }}>
      <Spin size="large" />
    </div>
  );
}

const { Text } = Typography;

const PREVIEW_DEFAULT_ROW_LIMIT = 25;

// --- UrlStateSync pure helpers -------------------------------------------

// Parse `?layers=` into an array to apply, or null when there's nothing new
// to apply (missing/empty param, or it already matches the current layers).
export function parseLayersParam(urlLayers, currentLayers) {
  if (!urlLayers) {return null;}
  const parsed = urlLayers.split(',').map((s) => s.trim()).filter(Boolean);
  if (!parsed.length) {return null;}
  if (parsed.join(',') === (currentLayers || []).join(',')) {return null;}
  return parsed;
}

// Resolve the `?schema=&table=` deep-link target to apply, or null when it
// already matches the current selection. Best-effort layer resolution from
// the cached catalog — an unresolved layer is harmless (see UrlStateSync).
export function resolveTableFromUrl(urlSchema, urlTable, selectedSchema, selectedTable, layerTables) {
  const nextSchema = urlSchema || '';
  const nextTable = urlTable || '';
  const curSchema = selectedSchema || '';
  const curTable = selectedTable || '';
  if (nextSchema === curSchema && nextTable === curTable) {return null;}
  const lyr = (layerTables || []).find(
    (t) => t.connector === nextSchema && t.table === nextTable,
  )?.layer;
  return { schema: nextSchema, table: nextTable, layer: lyr };
}

/**
 * DataCenter — unified data workspace for an end-user.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │  Warehouse strip (compact)  ·  Refresh                       │
 *   ├────────────────┬─────────────────────────────────────────────┤
 *   │                │ [ Catalog ] [ Data Preview ] [ SQL Workbench ] │
 *   │  Shared        │                                             │
 *   │  Vault         │  <embedded page for the active tab>         │
 *   │  Sidebar       │                                             │
 *   │  (tables)      │                                             │
 *   └────────────────┴─────────────────────────────────────────────┘
 *
 * The sidebar is a single source of truth for tables — its data and
 * selection state live in `VaultDataContext`. Each embedded page reads
 * from the same context and skips its own data fetch / sidebar so all
 * tabs stay in sync and we only hit /vault/erd once. Every tab drives the
 * sidebar in single-select mode.
 */
export default function DataCenter() {
  return (
    <VaultDataProvider>
      <UrlStateSync />
      <DataCenterInner />
    </VaultDataProvider>
  );
}

/**
 * UrlStateSync — bidirectional sync between the Data Center's view
 * state (`layer`, `schema` = selected connector, `table` = selected
 * table) and the URL's query string. Provides:
 *
 *   - **Restorability**: refreshing the page or bookmarking a URL
 *     preserves which layer/table the user was looking at.
 *   - **Deep linking**: other pages (Data Lineage Overview) can navigate
 *     into a specific table via `?layer=…&schema=…&table=…`.
 *   - **Back/forward**: browser history keeps each state change as a
 *     URL update so the back button rewinds the workspace state.
 *
 * Note: `tab` is read/written directly from the active <Tabs> in
 * DataCenterInner, not here. Activetab still uses URL as its source of
 * truth — this component only mirrors the vault-context fields.
 */
function UrlStateSync() {
  const v = useVaultData();
  const [params, setParams] = useSearchParams();

  // The set of sync'd query keys this component owns. Other keys (tab) are
  // ignored when comparing URLs so changes to them don't accidentally
  // re-trigger a URL→context apply.
  const SYNCED_KEYS = ['layers', 'schema', 'table'];
  const signatureFromParams = useCallback((p) => SYNCED_KEYS
    .map((k) => `${k}=${p.get(k) || ''}`)
    .join('&'), []);

  // The last URL signature we've **synchronized in either direction** —
  // updated when effect 2 writes to the URL (`context→URL`) and when
  // effect 1 consumes a URL change (`URL→context`). Effect 1 skips any
  // URL that already matches this ref so we don't fight context-side
  // corrections (notably VaultDataContext's auto-fallback when the user
  // picks an empty layer: it flips `layer` back, our effect 2 rewrites
  // the URL, and without this guard effect 1 would re-apply the stale
  // URL — producing an infinite silver↔bronze ping-pong).
  const lastSyncedRef = useRef(signatureFromParams(params));

  // URL → context. Only fires for URL changes we haven't already
  // synchronized (i.e. genuine external navigation: back/forward,
  // deep-link mount, shared URL paste).
  useEffect(() => {
    if (!v) {return;}
    const sig = signatureFromParams(params);
    if (sig === lastSyncedRef.current) {return;}
    lastSyncedRef.current = sig;

    // Multi-select layers via `?layers=bronze,silver,gold`. A legacy
    // single `?layer=` is intentionally ignored so the default
    // (bronze+silver+gold) stands; effect 2 rewrites the URL to `layers=`.
    if (v.setLayers) {
      const layers = parseLayersParam(params.get('layers'), v.layers);
      if (layers) {v.setLayers(layers);}
    }
    // Best-effort: resolve the table's layer from the cached catalog so
    // deep-links land on the correct `<layer>__<connector>` schema. When
    // the tables aren't loaded yet the resolver falls back to a cross-layer
    // match once they arrive, so an undefined layer here is harmless.
    if (v.selectTable) {
      const target = resolveTableFromUrl(
        params.get('schema'), params.get('table'), v.selectedSchema, v.selectedTable, v.layerTables,
      );
      if (target) {v.selectTable(target.schema, target.table, target.layer);}
    }
  }, [params, v, signatureFromParams]);

  // Context → URL. Reflects the current vault state in the query
  // string. `replace` mode so each state change updates the URL in
  // place rather than spamming the history stack.
  useEffect(() => {
    if (!v) {return;}
    const next = new URLSearchParams(params);
    let dirty = false;
    const writeOrDelete = (key, value) => {
      const current = next.get(key);
      if (value) {
        if (current !== value) { next.set(key, value); dirty = true; }
      } else if (current !== null) {
        next.delete(key); dirty = true;
      } else {
        // No value and nothing currently set for this key — nothing to do.
      }
    };
    writeOrDelete('layers', (v.layers || []).join(','));
    writeOrDelete('layer', '');  // drop the legacy single-layer param
    writeOrDelete('schema', v.selectedSchema || '');
    writeOrDelete('table', v.selectedTable || '');
    if (dirty) {
      lastSyncedRef.current = signatureFromParams(next);
      setParams(next, { replace: true });
    }
  }, [v?.layers, v?.selectedSchema, v?.selectedTable, params, setParams, v, signatureFromParams]);

  return null;
}

// Set of legal `?tab=` values. Keeps a typo'd URL from leaving the page
// stuck on a non-existent tab.
const TAB_KEYS = new Set(['catalog', 'preview', 'history', 'query']);

function DataCenterInner() {
  const [destinations, setDestinations] = useState(null); // null = loading
  const [searchParams, setSearchParams] = useSearchParams();

  // Active tab is URL-driven: `?tab=<key>` is the source of truth so
  // refresh / browser back / shared links restore the same view. Falls
  // back to Catalog when the URL is missing or carries an unknown key.
  const urlTab = searchParams.get('tab');
  const activeTab = (urlTab && TAB_KEYS.has(urlTab)) ? urlTab : 'catalog';
  const setActiveTab = useCallback((key) => {
    if (!TAB_KEYS.has(key)) {return;}
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', key);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const refreshDestinations = useCallback(async () => {
    try {
      const rows = await listDestinations();
      setDestinations(Array.isArray(rows) ? rows : []);
    } catch (err) {
      message.error(`Failed to load destinations: ${err.message}`);
      setDestinations([]);
    }
  }, []);

  useEffect(() => { refreshDestinations(); }, [refreshDestinations]);

  // Tabs render the embedded pages in `embedded` mode — they skip their
  // own data fetch + sidebar and read selection state from the vault
  // context. Wrapped in full-height flex divs so each pane gets a real
  // bounding box (otherwise it collapses to 0 px).
  //
  // MUST be memoised: a fresh array (or fresh React elements inside it)
  // makes antd `<Tabs>` re-mount the active pane on every parent render,
  // which retriggers the embedded pages' mount-time effects (listConnections,
  // etc.) and produces an infinite request loop.
  //
  // Mount-on-first-visit: tabs the user hasn't clicked yet render as an
  // empty placeholder so their heavy children (Monaco / ReactFlow / ELK)
  // never enter the tree on initial paint. Once visited, the pane stays
  // mounted (so switching back doesn't lose state). The synchronous
  // setState-during-render pattern ensures the newly-active tab is
  // marked visited in the *same* commit — otherwise the pane briefly
  // paints blank before a follow-up effect re-renders it with content.
  const [visitedTabs, setVisitedTabs] = useState(() => new Set([activeTab]));
  if (!visitedTabs.has(activeTab)) {
    setVisitedTabs(new Set([...visitedTabs, activeTab]));
  }
  const tabItems = useMemo(() => {
    const fillHeight = { height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 };
    const pane = (key, node) => (
      <div style={fillHeight}>
        {visitedTabs.has(key) ? (
          <Suspense fallback={<PaneSpinner />}>{node}</Suspense>
        ) : null}
      </div>
    );
    return [
      {
        key: 'catalog',
        label: <span><DatabaseOutlined /> Catalog</span>,
        children: pane('catalog', <DataExplorerPage embedded />),
      },
      {
        key: 'preview',
        label: <span><TableOutlined /> Data Preview</span>,
        children: pane('preview', <DataPreviewTab />),
      },
      {
        key: 'history',
        label: <span><HistoryOutlined /> Sync History</span>,
        children: pane('history', <SyncHistoryTab />),
      },
      {
        key: 'query',
        label: <span><ConsoleSqlOutlined /> SQL Workbench</span>,
        children: pane('query', <SqlWorkbenchTab />),
      },
    ];
  }, [visitedTabs]);

  if (destinations === null) {
    return (
      <div style={{ padding: 64, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  // No warehouse yet. It is registered from deploy config and auto-seeded by
  // pipeline-api on startup, so there is nothing for the user to fill in —
  // a wait-and-refresh hint is the whole of it.
  if (destinations.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Card style={{ marginBottom: 16 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space orientation="vertical" align="center">
                <Text strong>No warehouse connected</Text>
                <Text type="secondary">
                  The default warehouse is being provisioned. Refresh in a moment.
                </Text>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={refreshDestinations}
                  size="small"
                >
                  Refresh
                </Button>
              </Space>
            }
          />
        </Card>
      </div>
    );
  }

  const strip = (
    <div className="data-center-strip-stack">
      {destinations.map((d, idx) => {
        let typeLabel;
        if (d.type === 'postgres') {
          typeLabel = 'PostgreSQL';
        } else if (d.type === 'redshift') {
          typeLabel = 'Redshift';
        } else {
          typeLabel = d.type;
        }
        const isLast = idx === destinations.length - 1;
        return (
          <div key={d.id} className="data-center-strip">
            <div className="data-center-strip-left">
              <span className="data-center-strip-icon">
                <DatabaseOutlined />
              </span>
              <span className="data-center-strip-label">Warehouse:</span>
              <span className="data-center-strip-name">{d.name}</span>
              <span className="data-center-strip-type">{typeLabel}</span>
            </div>
            {isLast && (
              <div className="data-center-strip-right">
                <button
                  type="button"
                  className="data-center-strip-link"
                  onClick={refreshDestinations}
                  title="Refresh schemas"
                >
                  <ReloadOutlined /> Refresh
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // When the user double-clicks a table in Query mode, the sidebar fires
  // an event we forward to the SqlWorkbench via window-event (so we
  // don't have to plumb a ref through the tab content).
  const onDoubleClickTable = (t) => {
    if (!t) {return;}
    window.dispatchEvent(new CustomEvent('vault:insert-select', {
      detail: { schema: t.schema_name, table: t.table },
    }));
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {strip}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex' }}>
        {/* Shared left rail — visible on all tabs so users keep access to
            the schema/table tree wherever they are. */}
        <VaultSidebar
          tab={activeTab}
          width={340}
          onDoubleClickTable={activeTab === 'query' ? onDoubleClickTable : undefined}
        />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Tabs
            className="data-center-tabs"
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            tabBarStyle={{ padding: '0 16px', marginBottom: 0 }}
            style={{ height: '100%' }}
            destroyOnHidden={false}
            tabBarGutter={24}
          />
        </div>
      </div>
    </div>
  );
}

// --- DataPreviewTab pure helpers ------------------------------------------

// Which selection hint (if any) the toolbar should show: no table chosen at
// all, or one chosen but its warehouse schema not yet resolved. Mutually
// exclusive by construction (tableSelectedButUnresolved implies !hasSelection).
export function previewSelectionHint(hasSelection, tableSelectedButUnresolved) {
  if (tableSelectedButUnresolved) {return 'resolving';}
  if (!hasSelection) {return 'none';}
  return null;
}

// True while there's nothing to show yet — no result, not loading, no error.
export function isPreviewIdle(data, loading, error) {
  return !data && !loading && !error;
}

export function previewEmptyDescription(hasSelection) {
  return hasSelection ? 'Click Run to load rows.' : 'Pick a table in the sidebar, then click Run.';
}

// Cell render for the preview table: null/undefined -> em dash, objects ->
// pretty JSON, everything else -> its string form.
export function formatPreviewCellValue(val) {
  if (val == null) {return null;}
  if (typeof val === 'object') {return JSON.stringify(val);}
  return String(val);
}

// Row-array + column-index result into antd <Table> dataSource objects.
export function buildPreviewTableRows(rows, columns) {
  return rows.map((r, i) => {
    const obj = { _key: i };
    columns.forEach((c, idx) => { obj[c] = r[idx]; });
    return obj;
  });
}

function PreviewSelectionHint({ mode, connector, tableName }) {
  if (mode === 'none') {
    return <Text type="secondary">— select a table in the sidebar —</Text>;
  }
  if (mode === 'resolving') {
    return (
      <Space size={6}>
        <Spin size="small" />
        <Text type="secondary">Resolving {connector}.{tableName}…</Text>
      </Space>
    );
  }
  return null;
}

function PreviewResultsCard({ data, lastTarget, limit }) {
  return (
    <Card
      size="small"
      title={
        <Space>
          <TableOutlined />
          First {lastTarget?.limit ?? limit} rows
          {lastTarget && (
            <Tag color="purple">{lastTarget.schema}.{lastTarget.table}</Tag>
          )}
        </Space>
      }
    >
      {data.rows.length === 0 ? (
        <Empty description="Table is empty." />
      ) : (
        <Table
          size="small"
          scroll={{ x: 'max-content' }}
          rowKey={(_, i) => i}
          pagination={{ pageSize: 25, showSizeChanger: false }}
          dataSource={buildPreviewTableRows(data.rows, data.columns)}
          columns={data.columns.map((c) => ({
            title: c, dataIndex: c, ellipsis: true,
            render: (val) => {
              const formatted = formatPreviewCellValue(val);
              if (formatted === null) {return <Text type="secondary">—</Text>;}
              return typeof val === 'object'
                ? <code style={{ fontSize: 11 }}>{formatted}</code>
                : formatted;
            },
          }))}
        />
      )}
    </Card>
  );
}

/**
 * DataPreviewTab — Data Center "Data Preview" tab.
 *
 * The user picks a table in the shared Vault sidebar, chooses a row
 * limit (10/25/50/100), and clicks Run to fetch the rows. Unlike the
 * Catalog tab's old Preview sub-tab, this view never auto-loads data —
 * a query only fires when the user clicks Run.
 */
function DataPreviewTab() {
  // Resolve the warehouse schema_name from the lightweight layerTables /
  // medallion-name fallback so opening the Preview tab doesn't force a
  // full ERD fetch just to compute "<layer>__<connector>".
  const { schemaName, tableName, connector } = useSelectedSchemaName();
  const [limit, setLimit] = useState(PREVIEW_DEFAULT_ROW_LIMIT);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastTarget, setLastTarget] = useState(null);

  // Clear results whenever the user picks a different table — stale rows
  // from a previous selection would be misleading.
  useEffect(() => {
    setData(null);
    setError(null);
  }, [schemaName, tableName]);

  const run = useCallback(async () => {
    if (!schemaName || !tableName) {return;}
    setLoading(true);
    setError(null);
    try {
      const r = await getVaultData(schemaName, tableName, limit, 0);
      setData(r);
      setLastTarget({ schema: schemaName, table: tableName, limit });
    } catch (err) {
      setError(err.message || String(err));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [schemaName, tableName, limit]);

  const hasSelection = !!(schemaName && tableName);
  const tableSelectedButUnresolved = !!tableName && !schemaName;
  const hintMode = previewSelectionHint(hasSelection, tableSelectedButUnresolved);

  return (
    <div style={{ padding: 16, overflow: 'auto', height: '100%' }}>
      <Card
        size="small"
        title={<Space><TableOutlined /> Data Preview</Space>}
        style={{ marginBottom: 16 }}
      >
        <Space wrap align="center">
          <Text type="secondary">Rows:</Text>
          <Select
            size="small"
            value={limit}
            onChange={setLimit}
            style={{ width: 90 }}
            options={[10, 25, 50, 100].map((n) => ({ value: n, label: String(n) }))}
          />
          <Button
            type="primary"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={run}
            disabled={!hasSelection}
            loading={loading}
          >
            Run
          </Button>
          <PreviewSelectionHint mode={hintMode} connector={connector} tableName={tableName} />
        </Space>
      </Card>

      {error && (
        <Alert
          type="error"
          showIcon
          title="Couldn't load data"
          description={error}
          style={{ marginBottom: 16 }}
        />
      )}

      {isPreviewIdle(data, loading, error) && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={previewEmptyDescription(hasSelection)}
        />
      )}

      {data && <PreviewResultsCard data={data} lastTarget={lastTarget} limit={limit} />}
    </div>
  );
}

/**
 * Resolve the warehouse schema_name for the currently-selected table.
 * Falls back to the medallion convention `<layer>__<connector>` if the
 * cached layerTables don't yet have an entry (e.g. the user opened the
 * tab before expanding the schema).
 */
function useSelectedSchemaName() {
  const v = useVaultData();
  // Schema resolution is centralised in VaultDataContext (it has the full
  // catalog + the layer the user actually clicked), so a table in any shown
  // layer — not just layers[0] — resolves to its real `<layer>__<connector>`.
  return {
    schemaName: v?.selectedSchemaName || '',
    tableName: v?.selectedTable || '',
    connector: v?.selectedSchema || '',
    layer: v?.selectedSchemaLayer || v?.layer || '',
  };
}

/**
 * SqlWorkbenchTab — thin wrapper around the lazy <SqlWorkbench> that
 * pre-fills the editor with `SELECT * FROM <selected_table>` when the
 * user opens the SQL tab with a table already chosen in the sidebar.
 */
function SqlWorkbenchTab() {
  const { schemaName, tableName } = useSelectedSchemaName();
  const defaultQuery = (schemaName && tableName)
    ? `SELECT *\nFROM "${schemaName}"."${tableName}"\nLIMIT 100;\n`
    : undefined;
  return <SqlWorkbench embedded defaultQuery={defaultQuery} />;
}

// --- SyncHistoryTab pure helpers ------------------------------------------

// `connector` is derived from the warehouse schema name (e.g.
// `bronze__claude_code` -> `claude_code`), but `connector_name` on the
// connection is the hyphenated catalog id (`claude-code`). Compare with
// `-`/`_` normalized so the bronze / silver views resolve their owning
// connection.
export function matchConnectionForConnector(connections, connector) {
  if (!connections || !connector) {return null;}
  const norm = (s) => String(s || '').replace(/-/g, '_');
  const target = norm(connector);
  return connections.find((c) => norm(c.connector_name) === target) || null;
}

// The per-stream run reshaping now lives in `services/syncHistory.js` so Data
// Explorer's Sync History tab reads Dagster the same way this page does.
// Re-exported unchanged: DataCenter.test.js pins them from here.
export {
  buildSyncHistoryRow, buildSyncHistoryRows, filterSyncRunsForStream,
  resolveStreamAndPrefix, stripSyncAssetPrefix, syncHistoryStatusColor,
};

// Which body the Sync History card should render.
export function resolveSyncHistoryView(tableName, connection, historyLoading, historyLength) {
  if (!tableName) {return 'no-table';}
  if (!connection) {return 'no-connection';}
  if (historyLoading) {return 'loading';}
  if (historyLength === 0) {return 'empty';}
  return 'table';
}

function SyncHistoryBody({ view, history, columns }) {
  if (view === 'no-table') {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Select a table in the sidebar to see its sync runs."
      />
    );
  }
  if (view === 'no-connection') {
    return (
      <Alert
        type="info"
        showIcon
        title="Sync history isn't available — no active connection found for this table's source schema."
      />
    );
  }
  if (view === 'loading') {
    return <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>;
  }
  if (view === 'empty') {
    return <Empty description="No sync runs recorded for this table yet." />;
  }
  return (
    <Table
      size="small"
      rowKey={(r, i) => r.run_id ? `${r.run_id}-${i}` : i}
      dataSource={history}
      columns={columns}
      pagination={{ pageSize: 20, showSizeChanger: false }}
    />
  );
}

/**
 * SyncHistoryTab — Data Center "Sync History" tab.
 *
 * Was a sub-tab of the Catalog view; promoted to its own top-level tab
 * so it stops re-mounting every time the user changes table selection
 * inside the Catalog details pane. Connections are fetched the first
 * time the tab is opened (mount-on-first-visit) and cached for the rest
 * of the session.
 */
function SyncHistoryTab() {
  const v = useVaultData();
  const connector = v?.selectedSchema || '';
  const tableName = v?.selectedTable || '';

  const [connections, setConnections] = useState(null); // null = not yet loaded
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setConnectionsLoading(true);
    listConnections()
      .then((c) => { if (!cancelled) {setConnections(c || []);} })
      .catch(() => { if (!cancelled) {setConnections([]);} })
      .finally(() => { if (!cancelled) {setConnectionsLoading(false);} });
    return () => { cancelled = true; };
  }, []);

  const connection = useMemo(
    () => matchConnectionForConnector(connections, connector),
    [connections, connector],
  );

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // History is sourced directly from Dagster. The legacy
  // /syncs/streams/history endpoint reads `sync_stream_runs`, which the
  // Dagster cutover stopped populating — so it always returned []. We
  // fetch the connection's runs, filter to those that touched the
  // selected stream, then pull each matching run's materializations to
  // get the per-stream rowcount + timing.
  const load = useCallback(async () => {
    if (!connection || !tableName) {
      setHistory([]);
      return;
    }
    setHistoryLoading(true);
    try {
      const runs = await listDagitRuns({
        connectionId: connection.id,
        limit: 50,
      }).catch(() => []);
      const { stream, prefix } = resolveStreamAndPrefix(tableName, connector);
      const relevant = filterSyncRunsForStream(runs, stream, prefix);
      const details = await Promise.all(relevant.map(
        (r) => getDagitRun(r.id).catch(() => null),
      ));
      setHistory(buildSyncHistoryRows(relevant, details, stream, prefix));
    } catch (err) {
      message.warning(`Couldn't load sync history: ${err.message}`);
    } finally {
      setHistoryLoading(false);
    }
  }, [connection, tableName, connector]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    {
      title: 'Run', dataIndex: 'run_id', width: 220,
      // The id itself is the useful part and stays visible either way; only the
      // hyperlink depends on the Dagit proxy being mounted on this deployment.
      render: (val) => {
        if (!val) {return '—';}
        const shortId = <code style={{ fontSize: 11 }}>{String(val).slice(0, 8)}</code>;
        if (!isDagitEnabled()) {return shortId;}
        return (
          <a
            href={dagsterRunUrl(val)}
            target="_blank"
            rel="noreferrer"
            title="Open this run in Dagster"
            onClick={(e) => { e.preventDefault(); openDagsterRun(val); }}
          >
            {shortId}
            <ExportOutlined style={{ fontSize: 11, marginLeft: 4 }} />
          </a>
        );
      },
    },
    {
      title: 'Status', dataIndex: 'status', width: 120,
      // Status comes from Dagster: success/failure/started/queued/canceled/...
      // Legacy values (succeeded/failed/running/cancelled) are mapped too
      // for tolerance in case a different code path feeds this column.
      render: (val) => <Tag color={syncHistoryStatusColor(val)}>{val || '—'}</Tag>,
    },
    {
      title: 'Rows', dataIndex: 'rows_loaded', width: 100, align: 'right',
      render: (val) => val != null ? val.toLocaleString() : '—',
    },
    {
      title: 'Started', dataIndex: 'started_at', width: 170,
      render: (val) => val ? new Date(val).toLocaleString() : '—',
    },
    {
      title: 'Finished', dataIndex: 'finished_at', width: 170,
      render: (val) => val ? new Date(val).toLocaleString() : '—',
    },
    {
      title: 'Error', dataIndex: 'error_message',
      render: (val) => val
        ? <Text type="danger" ellipsis style={{ maxWidth: 320 }}>{val}</Text>
        : '—',
    },
  ];

  if (connectionsLoading && connections === null) {
    return (
      <div style={{ padding: 64, textAlign: 'center' }}><Spin size="large" /></div>
    );
  }

  return (
    <div style={{ padding: 16, overflow: 'auto', height: '100%' }}>
      <Card
        size="small"
        title={
          <Space>
            <HistoryOutlined />
            {tableName ? <>Sync history for <code>{tableName}</code></> : 'Sync history'}
          </Space>
        }
        extra={
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
            loading={historyLoading}
            disabled={!connection || !tableName}
          >
            Refresh
          </Button>
        }
      >
        <SyncHistoryBody
          view={resolveSyncHistoryView(tableName, connection, historyLoading, history.length)}
          history={history}
          columns={columns}
        />
      </Card>
    </div>
  );
}
